/**
 * Phase 2A · Inline section editor drawer.
 *
 * Opens when the owner taps "Add" or "Edit" on any Digital Home section.
 * Renders a focused form for that ONE section, PUTs to
 * `/api/me/athlete/profile`, and calls onSaved(freshAthlete) so the
 * parent page can re-render with the updated data in place.
 *
 * Each section has a small custom form — no JSON editor exposed to the
 * athlete. Keeps the UX premium and forgiving.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Trash2, Plus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTION_TITLE = {
  milestones: 'Career Milestones',
  awards: 'Awards', academics: 'Academics', recruiting: 'Recruiting',
  media_kit: 'Media Kit', press: 'Press', events: 'Upcoming Events',
  bookings: 'Bookings', fan_club: 'Fan Club', newsletter: 'Newsletter',
  merch: 'Merch', game_film: 'Game Film',
};

const Input = ({ label, value, onChange, type = 'text', placeholder, ...rest }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-widest text-white/40">{label}</span>
    <input type={type} value={value ?? ''} onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-400/60"
      {...rest} />
  </label>
);

const Textarea = ({ label, value, onChange, rows = 3, placeholder }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-widest text-white/40">{label}</span>
    <textarea rows={rows} value={value ?? ''} onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm resize-none focus:outline-none focus:border-blue-400/60" />
  </label>
);

const Toggle = ({ label, value, onChange }) => (
  <label className="flex items-center justify-between gap-3 p-3 rounded-lg bg-white/[0.03] ring-1 ring-white/10 cursor-pointer">
    <span className="text-sm text-white">{label}</span>
    <button type="button" onClick={() => onChange(!value)}
      className={`w-10 h-6 rounded-full transition ${value ? 'bg-blue-500' : 'bg-white/15'}`}>
      <span className={`block w-5 h-5 rounded-full bg-white transition-transform ${value ? 'translate-x-4' : 'translate-x-0.5'}`} style={{ marginTop: 2 }} />
    </button>
  </label>
);

const ListEditor = ({ items, onChange, template, renderRow, addLabel = 'Add row' }) => (
  <div className="space-y-2">
    {(items || []).map((row, i) => (
      <div key={i} className="p-3 rounded-lg bg-white/[0.03] ring-1 ring-white/10 relative">
        <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))}
          className="absolute top-2 right-2 w-6 h-6 rounded-full bg-white/5 hover:bg-red-500/20 inline-flex items-center justify-center transition">
          <Trash2 className="w-3 h-3 text-white/50" />
        </button>
        {renderRow(row, (updated) => onChange(items.map((r, j) => j === i ? updated : r)))}
      </div>
    ))}
    <button type="button" onClick={() => onChange([...(items || []), { ...template }])}
      className="w-full inline-flex items-center justify-center gap-2 p-3 rounded-lg bg-white/[0.02] ring-1 ring-dashed ring-white/15 hover:ring-blue-400/40 transition text-sm text-white/60 hover:text-white">
      <Plus className="w-4 h-4" /> {addLabel}
    </button>
  </div>
);

/* ---------- Per-section forms ---------------------------------------- */

// iter124 · Athlete-authored career milestones. Same shape as the
// admin/founder-authored ones so they merge into a single timeline.
const MilestonesForm = ({ value, onChange }) => (
  <ListEditor items={value || []} onChange={onChange} addLabel="Add milestone"
    template={{ year: new Date().getFullYear(), label: '', detail: '', team: '', current: false }}
    renderRow={(row, upd) => (
      <div className="space-y-2">
        <Input label="Milestone / Moment" value={row.label} onChange={(v) => upd({ ...row, label: v })} placeholder="Major League Debut" />
        <div className="grid grid-cols-2 gap-2">
          <Input label="Year" type="number" value={row.year} onChange={(v) => upd({ ...row, year: parseInt(v, 10) || undefined })} placeholder="2026" />
          <Input label="Team / Program" value={row.team} onChange={(v) => upd({ ...row, team: v })} placeholder="Atlanta Braves" />
        </div>
        <Textarea label="Short detail (optional)" rows={2} value={row.detail} onChange={(v) => upd({ ...row, detail: v })} placeholder="Started at 2B, went 2-for-4 with an RBI." />
        <label className="inline-flex items-center gap-2 text-xs text-white/70 mt-1">
          <input type="checkbox" checked={!!row.current} onChange={(e) => upd({ ...row, current: e.target.checked })}
            className="w-4 h-4 rounded bg-white/5 border border-white/20 accent-emerald-500" />
          Mark as current / ongoing (adds a green pill on the timeline)
        </label>
      </div>
    )} />
);

const AwardsForm = ({ value, onChange }) => (
  <ListEditor items={value || []} onChange={onChange} addLabel="Add award"
    template={{ year: new Date().getFullYear(), label: '', org: '' }}
    renderRow={(row, upd) => (
      <div className="space-y-2">
        <Input label="Award / Title" value={row.label} onChange={(v) => upd({ ...row, label: v })} placeholder="All-State First Team" />
        <div className="grid grid-cols-2 gap-2">
          <Input label="Year" type="number" value={row.year} onChange={(v) => upd({ ...row, year: parseInt(v, 10) || undefined })} placeholder="2026" />
          <Input label="Org / Level" value={row.org} onChange={(v) => upd({ ...row, org: v })} placeholder="USA Today" />
        </div>
      </div>
    )} />
);

const AcademicsForm = ({ value, onChange }) => {
  const v = value || {};
  const set = (k, x) => onChange({ ...v, [k]: x });
  return (
    <div className="grid grid-cols-2 gap-3">
      <Input label="GPA" value={v.gpa} onChange={(x) => set('gpa', x)} placeholder="4.1" />
      <Input label="Grad Year" type="number" value={v.graduation_year} onChange={(x) => set('graduation_year', parseInt(x, 10) || undefined)} placeholder="2027" />
      <Input label="Major" value={v.major} onChange={(x) => set('major', x)} placeholder="Undecided" />
      <Input label="School" value={v.school} onChange={(x) => set('school', x)} placeholder="Central High" />
      <Input label="SAT" type="number" value={v.sat} onChange={(x) => set('sat', parseInt(x, 10) || undefined)} placeholder="1480" />
      <Input label="ACT" type="number" value={v.act} onChange={(x) => set('act', parseInt(x, 10) || undefined)} placeholder="34" />
      <Input label="Class Rank" value={v.class_rank} onChange={(x) => set('class_rank', x)} placeholder="5/312" />
    </div>
  );
};

const RecruitingForm = ({ value, onChange }) => {
  const v = value || {};
  const set = (k, x) => onChange({ ...v, [k]: x });
  return (
    <div className="space-y-3">
      <label className="block">
        <span className="text-[10px] uppercase tracking-widest text-white/40">Status</span>
        <select value={v.status || ''} onChange={(e) => set('status', e.target.value)}
          className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm">
          <option value="">Select…</option>
          <option value="open">Open</option>
          <option value="committed">Committed</option>
          <option value="closed">Closed</option>
        </select>
      </label>
      <Input label="Positions (comma-separated)" value={(v.positions || []).join(', ')}
        onChange={(x) => set('positions', x.split(',').map((s) => s.trim()).filter(Boolean))} />
      <Input label="Coach contact email" type="email" value={v.contact_coach_email} onChange={(x) => set('contact_coach_email', x)} />
      <Input label="Film link" value={v.film_link} onChange={(x) => set('film_link', x)} placeholder="https://hudl.com/…" />
      <div className="grid grid-cols-2 gap-3">
        <Input label="Height" value={v.height} onChange={(x) => set('height', x)} placeholder="6'2&quot;" />
        <Input label="Weight (lb)" type="number" value={v.weight_lbs} onChange={(x) => set('weight_lbs', parseInt(x, 10) || undefined)} />
      </div>
    </div>
  );
};

const MediaKitForm = ({ value, onChange }) => {
  const v = value || {};
  const set = (k, x) => onChange({ ...v, [k]: x });
  return (
    <div className="space-y-3">
      <Textarea label="One-liner (brand pitch)" value={v.one_liner} onChange={(x) => set('one_liner', x)}
        placeholder="What brands should know about you in one sentence." />
      <Input label="Media kit PDF URL" value={v.pdf_url} onChange={(x) => set('pdf_url', x)} />
      <Input label="Rates (public label)" value={v.rates} onChange={(x) => set('rates', x)} placeholder="From $2,500 · IG post" />
    </div>
  );
};

const PressForm = ({ value, onChange }) => (
  <ListEditor items={value || []} onChange={onChange} addLabel="Add press hit"
    template={{ outlet: '', headline: '', url: '', date: '', kind: 'article' }}
    renderRow={(row, upd) => (
      <div className="space-y-2">
        <Input label="Outlet" value={row.outlet} onChange={(v) => upd({ ...row, outlet: v })} placeholder="ESPN" />
        <Input label="Headline" value={row.headline} onChange={(v) => upd({ ...row, headline: v })} />
        <Input label="URL" value={row.url} onChange={(v) => upd({ ...row, url: v })} />
        <div className="grid grid-cols-2 gap-2">
          <Input label="Date" value={row.date} onChange={(v) => upd({ ...row, date: v })} placeholder="2026-02-14" />
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-white/40">Kind</span>
            <select value={row.kind || 'article'} onChange={(e) => upd({ ...row, kind: e.target.value })}
              className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm">
              <option value="article">Article</option>
              <option value="podcast">Podcast</option>
              <option value="interview">Interview</option>
              <option value="video">Video</option>
            </select>
          </label>
        </div>
      </div>
    )} />
);

const EventsForm = ({ value, onChange }) => (
  <ListEditor items={value || []} onChange={onChange} addLabel="Add event"
    template={{ date: '', title: '', location: '', url: '' }}
    renderRow={(row, upd) => (
      <div className="space-y-2">
        <Input label="Title" value={row.title} onChange={(v) => upd({ ...row, title: v })} />
        <div className="grid grid-cols-2 gap-2">
          <Input label="Date" type="date" value={row.date} onChange={(v) => upd({ ...row, date: v })} />
          <Input label="Location" value={row.location} onChange={(v) => upd({ ...row, location: v })} />
        </div>
        <Input label="URL (optional)" value={row.url} onChange={(v) => upd({ ...row, url: v })} />
      </div>
    )} />
);

const BookingsForm = ({ value, onChange }) => {
  const v = value || { enabled: false };
  const set = (k, x) => onChange({ ...v, [k]: x });
  return (
    <div className="space-y-3">
      <Toggle label="Open for bookings" value={!!v.enabled} onChange={(x) => set('enabled', x)} />
      <Input label="Contact email" type="email" value={v.contact_email} onChange={(x) => set('contact_email', x)} />
      <Input label="Categories (comma-separated)" value={(v.categories || []).join(', ')}
        onChange={(x) => set('categories', x.split(',').map((s) => s.trim()).filter(Boolean))}
        placeholder="brand shoots, speaking, camps" />
      <div className="grid grid-cols-2 gap-3">
        <Input label="Rate (public label)" value={v.hourly_rate} onChange={(x) => set('hourly_rate', x)} placeholder="From $500/hr" />
        <Input label="Response time (hrs)" type="number" value={v.turnaround_hours} onChange={(x) => set('turnaround_hours', parseInt(x, 10) || undefined)} />
      </div>
    </div>
  );
};

const FanClubForm = ({ value, onChange }) => {
  const v = value || { enabled: false, platforms: [] };
  const set = (k, x) => onChange({ ...v, [k]: x });
  return (
    <div className="space-y-3">
      <Toggle label="Enable fan hub" value={!!v.enabled} onChange={(x) => set('enabled', x)} />
      <Textarea label="Tagline" value={v.tagline} onChange={(x) => set('tagline', x)}
        placeholder="Join the community · early drops · behind the scenes." />
      <ListEditor items={v.platforms || []} onChange={(x) => set('platforms', x)} addLabel="Add platform"
        template={{ name: '', url: '' }}
        renderRow={(row, upd) => (
          <div className="space-y-2">
            <Input label="Platform name" value={row.name} onChange={(x) => upd({ ...row, name: x })} placeholder="Discord" />
            <Input label="URL" value={row.url} onChange={(x) => upd({ ...row, url: x })} />
          </div>
        )} />
    </div>
  );
};

const NewsletterForm = ({ value, onChange }) => {
  const v = value || { enabled: false };
  const set = (k, x) => onChange({ ...v, [k]: x });
  return (
    <div className="space-y-3">
      <Toggle label="Enable newsletter signup" value={!!v.enabled} onChange={(x) => set('enabled', x)} />
      <Input label="Provider" value={v.provider} onChange={(x) => set('provider', x)} placeholder="Beehiiv / Substack / etc" />
      <Input label="Signup URL" value={v.signup_url} onChange={(x) => set('signup_url', x)} />
    </div>
  );
};

const MerchForm = ({ value, onChange }) => (
  <ListEditor items={value || []} onChange={onChange} addLabel="Add product"
    template={{ name: '', image: '', price: '', url: '' }}
    renderRow={(row, upd) => (
      <div className="space-y-2">
        <Input label="Name" value={row.name} onChange={(x) => upd({ ...row, name: x })} />
        <Input label="Image URL" value={row.image} onChange={(x) => upd({ ...row, image: x })} />
        <div className="grid grid-cols-2 gap-2">
          <Input label="Price" value={row.price} onChange={(x) => upd({ ...row, price: x })} placeholder="$45" />
          <Input label="Buy URL" value={row.url} onChange={(x) => upd({ ...row, url: x })} />
        </div>
      </div>
    )} />
);

const GameFilmForm = ({ value, onChange }) => (
  <ListEditor items={value || []} onChange={onChange} addLabel="Add game film"
    template={{ opponent: '', date: '', url: '', thumb: '' }}
    renderRow={(row, upd) => (
      <div className="space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <Input label="Opponent" value={row.opponent} onChange={(x) => upd({ ...row, opponent: x })} />
          <Input label="Date" type="date" value={row.date} onChange={(x) => upd({ ...row, date: x })} />
        </div>
        <Input label="Video URL" value={row.url} onChange={(x) => upd({ ...row, url: x })} />
        <Input label="Thumb URL (optional)" value={row.thumb} onChange={(x) => upd({ ...row, thumb: x })} />
        <Input label="Duration (optional)" value={row.duration} onChange={(x) => upd({ ...row, duration: x })} placeholder="42:10" />
      </div>
    )} />
);

const SECTION_FORM = {
  milestones: MilestonesForm,
  awards: AwardsForm, academics: AcademicsForm, recruiting: RecruitingForm,
  media_kit: MediaKitForm, press: PressForm, events: EventsForm,
  bookings: BookingsForm, fan_club: FanClubForm, newsletter: NewsletterForm,
  merch: MerchForm, game_film: GameFilmForm,
};

// iter124 · Section id → athlete-doc field key. Almost every section
// uses the id 1:1 as the field name; `milestones` is the exception
// because the underlying field is `career_milestones` (shared with
// the auto-log system + hero timeline arc).
const FIELD_KEY = {
  milestones: 'career_milestones',
};
const fieldKey = (id) => FIELD_KEY[id] || id;

/* ---------- Root drawer ---------------------------------------------- */
const AthleteSectionEditorDrawer = ({ athlete, sectionId, onClose, onSaved }) => {
  const [value, setValue] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!sectionId) { setValue(null); return; }
    const key = fieldKey(sectionId);
    setValue(athlete?.[key] ?? (['milestones','awards','press','events','merch','game_film'].includes(sectionId) ? [] : {}));
  }, [sectionId, athlete]);

  if (!sectionId) return null;
  const Form = SECTION_FORM[sectionId];
  if (!Form) return null;
  const title = SECTION_TITLE[sectionId] || sectionId;

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(
        `${API}/me/athlete/profile`,
        { updates: { [fieldKey(sectionId)]: value } },
        { withCredentials: true },
      );
      toast.success(`${title} saved`);
      onSaved?.(r.data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-sm flex justify-end" onClick={onClose} data-testid="section-editor-drawer">
      <div className="w-full max-w-lg h-full bg-[#0a0a0a] ring-1 ring-white/15 overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#0a0a0a] px-5 py-4 border-b border-white/10 flex items-center justify-between z-10">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-blue-300 font-bold">Edit</p>
            <h3 className="font-display text-2xl uppercase text-white leading-none mt-0.5">{title}</h3>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 inline-flex items-center justify-center" data-testid="close-editor">
            <X className="w-4 h-4 text-white" />
          </button>
        </div>
        <div className="p-5">
          <Form value={value} onChange={setValue} />
        </div>
        <div className="sticky bottom-0 bg-[#0a0a0a] px-5 py-4 border-t border-white/10 flex gap-2 justify-end">
          <button onClick={onClose} disabled={saving}
            className="px-5 py-2.5 rounded-full bg-white/5 ring-1 ring-white/10 text-white text-sm hover:bg-white/10 transition">
            Cancel
          </button>
          <button onClick={save} disabled={saving}
            className="px-6 py-2.5 rounded-full bg-white text-black font-bold text-sm hover:bg-blue-300 transition disabled:opacity-50"
            data-testid="save-section">
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AthleteSectionEditorDrawer;
